import { createContext, useReducer } from 'react';
import type { ReactElement, ReactNode } from 'react';

import CurrentUser from 'models/CurrentUser';
import FetchState from 'models/FetchState';

type Props = { children: ReactNode; };
type Dispatcher = (action: Action) => void;

type Action =
  | { type: 'reload'; data: CurrentUser; }
  | { type: 'setFetchState'; data: FetchState; };

type ContextData = {
  currentUser: CurrentUser;
  fetchState: FetchState;
};

type ContextValue = ContextData & { dispatch: Dispatcher };

const UserContext = createContext<ContextValue | null>(null);

function currentUserReducer(
  value: ContextValue,
  action: Action,
): ContextData {
  const { currentUser, fetchState } = value;

  switch (action.type) {
    case 'reload': {
      return {
        fetchState,
        currentUser: new CurrentUser(action.data),
      };
    }

    case 'setFetchState': {
      return {
        currentUser,
        fetchState: action.data,
      };
    }

    default: {
      return value;
    }
  }
}

function CurrentUserProvider({ children }: Props): ReactElement {
  const [value, dispatch] = useReducer(currentUserReducer, {
    fetchState: FetchState.IN_PROGRESS,
    currentUser: new CurrentUser(),
  });

  const { currentUser, fetchState } = value;

  return (
    <UserContext.Provider value={{ currentUser, dispatch, fetchState }}>
      {children}
    </UserContext.Provider>
  );
}

export type { ContextValue };
export { UserContext, CurrentUserProvider };
